import { Injectable, inject, signal } from '@angular/core';
import { AngularFireAnalytics } from '@angular/fire/compat/analytics';
import { CookiePreferences } from 'src/app/interfaces';
import { environment } from 'src/environments/environment';

/** GA4 measurement id.*/
const GA_MEASUREMENT_ID = environment.analytics.measurementId;

declare global {
  interface Window {
    dataLayer?: any[];
    gtag?: (...args: any[]) => void;
  }
}

/**
 * Loads analytics tags only once the user has agreed to them, and is the single place that injects
 * Google Analytics. `index.html` carries no tracking tag: a script in `<head>` runs before Angular
 * exists, so consent could never gate it. Anything added later belongs here, behind the same check.
 *
 * Three mechanisms, because they fail differently:
 * - **Consent Mode v2** is denied *before* the tag is injected, so no race can leak a hit with
 *   storage enabled.
 * - **Firebase Analytics is not loaded at all** until consent. `AngularFireAnalyticsModule` is not
 *   imported anywhere, since its constructor initialises the SDK and injects gtag.js — and the
 *   `AngularFireAnalytics` proxy is lazy, so *any* call on it is enough to load the script.
 * - **`COLLECTION_ENABLED: false`** in `AppModule` keeps the SDK collecting nothing even once up.
 */
@Injectable({ providedIn: 'root' })
export class TrackingLoaderService {
  private readonly analytics = inject(AngularFireAnalytics);

  private gtagInjected = false;
  private consentDefaultsSet = false;
  /** Whether the Firebase Analytics SDK has been brought up. See `apply()` for why this matters. */
  private firebaseAnalyticsStarted = false;
  private readonly analyticsGranted = signal(false);

  /**
   * Whether analytics is currently permitted. A signal so callers that must wait for the answer —
   * the deferred `app_open` event, for one — react when it arrives rather than sampling too early
   * and dropping the event.
   */
  public readonly analyticsAllowed = this.analyticsGranted.asReadonly();

  /**
   * Apply the user's cookie choice to every tag this app can load.
   *
   * Safe to call repeatedly and in any order relative to consent resolving: the denied defaults go
   * out on the first call whatever the answer, so a later grant is an *update* to an already-denied
   * baseline rather than the first thing Google hears.
   */
  public apply(preferences: CookiePreferences): void {
    const wasGranted = this.analyticsGranted();
    this.ensureConsentDefaults();
    this.updateConsent(preferences);

    if (preferences.analytics) {
      this.injectGtag();
      this.enableFirebaseAnalytics();
    } else {
      if (wasGranted) {
        this.clearAnalyticsCookies();
      }
      // Only ever disable something previously enabled: any call on the lazy `AngularFireAnalytics`
      // proxy — `setAnalyticsCollectionEnabled(false)` included — imports firebase/analytics and
      // injects gtag.js, loading the very script a refusal exists to prevent.
      if (this.firebaseAnalyticsStarted) {
        this.analytics.setAnalyticsCollectionEnabled(false).catch(() => {});
      }
    }

    this.analyticsGranted.set(preferences.analytics);
  }

  /**
   * Bring Firebase Analytics up. This is the call that loads the SDK — hence the flag, so a later
   * withdrawal knows there is something to switch off. Firebase cannot be un-loaded once
   * initialised, so withdrawal is honoured by disabling collection and denying storage.
   */
  private enableFirebaseAnalytics(): void {
    this.firebaseAnalyticsStarted = true;
    this.analytics.setAnalyticsCollectionEnabled(true).catch(() => {
      // Analytics being unavailable must never break the app; there is nothing to recover here.
    });
  }

  /**
   * Delete the cookies GA already set, on withdrawal: denying `analytics_storage` stops new ones
   * but leaves these, and withdrawal has to undo the processing rather than only stop it. Tries
   * every parent domain, since GA registers `_ga` on the registrable domain rather than the host.
   */
  private clearAnalyticsCookies(): void {
    const names = document.cookie
      .split(';')
      .map(entry => entry.split('=')[0].trim())
      .filter(name => name === '_ga' || name.startsWith('_ga_') || name.startsWith('_gid'));
    const labels = location.hostname.split('.');
    const domains = ['', ...labels.map((_, i) => `.${labels.slice(i).join('.')}`)];
    for (const name of names) {
      for (const domain of domains) {
        document.cookie =
          `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT` +
          (domain ? `; domain=${domain}` : '');
      }
    }
  }

  /**
   * Install Google's documented `gtag` shim, once. It must push the `arguments` object, not an
   * array: gtag.js recognises commands by that shape, and a rest-parameter rewrite would leave the
   * consent defaults below silently ignored.
   */
  private ensureDataLayer(): void {
    window.dataLayer = window.dataLayer || [];
    if (window.gtag) return;
    window.gtag = function () {
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer!.push(arguments);
    };
  }

  /**
   * Emit the denied-by-default consent state, which must happen before any Google tag loads. Called
   * on every `apply()` but effective once: re-declaring defaults would reset the user's choice.
   */
  private ensureConsentDefaults(): void {
    this.ensureDataLayer();
    if (this.consentDefaultsSet) return;
    window.gtag!('consent', 'default', {
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
      analytics_storage: 'denied',
    });
    window.gtag!('js', new Date());
    this.consentDefaultsSet = true;
  }

  private updateConsent(preferences: CookiePreferences): void {
    window.gtag!('consent', 'update', {
      analytics_storage: preferences.analytics ? 'granted' : 'denied',
      // GA4 honours these today (they suppress Google Signals and ads data), and they are what a
      // future advertising tag would read, so the marketing toggle is not a placeholder.
      ad_storage: preferences.marketing ? 'granted' : 'denied',
      ad_user_data: preferences.marketing ? 'granted' : 'denied',
      ad_personalization: preferences.marketing ? 'granted' : 'denied',
    });
  }

  private injectGtag(): void {
    if (this.gtagInjected) return;
    this.gtagInjected = true;

    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
    document.head.appendChild(script);

    window.gtag!('config', GA_MEASUREMENT_ID, {
      anonymize_ip: true,
      allow_ad_personalization_signals: false,
      client_id_dimension: environment.clientId,
    });
  }
}
